import { SetStateAction, useState } from "react";

import { useTranslation } from "react-i18next";

import { useAuth } from "../../../../../../../context/Auth/AuthProvider";
import { USER_PERMISSIONS } from "../../../../../../../roles/types/enums";
import { COMPANY_SETTINGS, SettingsState } from "../../../../../types";
import SettingsTypeWrapper from "../SettingsTypeWrapper/SettingsTypeWrapper";
import useCompaniesSettings from "../hooks/useCompaniesSettings";
import useHandleToggleSettings from "../hooks/useHandleToggleSettings";

interface CompanySettingsProps {
  detailsData: any;
}

const CompanySettings = ({ detailsData }: CompanySettingsProps) => {
  const { getAccessStatus } = useAuth();
  const { t } = useTranslation();

  const canManageCompaniesSettings = getAccessStatus(
    USER_PERMISSIONS.ManageCompanies
  );

  const hasOverdraftToggle = detailsData?.overDraftEnabled !== undefined;

  const { handleToggleCompanySettings, handleToggleCreditsOverdraft } =
    useHandleToggleSettings({ detailsData });

  const {
    generateDriversSettings,
    generateVehiclesSettings,
    generateCreditsOverdraftSettings
  } = useCompaniesSettings({ detailsData });

  const [settings, setSettings] = useState<SettingsState>({
    [COMPANY_SETTINGS.AutoAssignVehicleRegistrationNumber]:
      detailsData?.autoAssignVrn ?? false,

    [COMPANY_SETTINGS.AutoInitialVehicleDownload]:
      detailsData?.autoInitialVehicleDownload ?? false,

    [COMPANY_SETTINGS.AutoAssignGlobalScheduleToVehicle]:
      detailsData?.autoAssignGlobalScheduleToVehicle ?? false,

    [COMPANY_SETTINGS.AutomaticDriverRegistrationEnabled]:
      detailsData?.automaticDriverRegistrationEnabled ?? false,

    [COMPANY_SETTINGS.AutoDriverFileDownload]:
      detailsData?.autoDriverFileDownload ?? false,

    [COMPANY_SETTINGS.AutoAssignGlobalScheduleToDriver]:
      detailsData?.autoAssignGlobalScheduleToDriver ?? false
  });

  const updateSetting = (
    setting: COMPANY_SETTINGS,
    value: SetStateAction<boolean>
  ) => {
    setSettings(prev => ({
      ...prev,
      [setting]: value
    }));
  };

  const handleSettingChange = (
    setting: COMPANY_SETTINGS,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    handleToggleCompanySettings(event, setting, value =>
      updateSetting(setting, value)
    );
  };

  const onToggle =
    (setting: COMPANY_SETTINGS) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      handleSettingChange(setting, event);
    };

  const [isCreditsOverdraftChecked, setIsCreditsOverdraftChecked] = useState(
    detailsData?.overDraftEnabled ?? false
  );

  const [overdraftInputValue, setOverdraftInputValue] = useState<string>(
    detailsData?.overDraftAmount
  );

  const onToggleCreditsOverdraft = (
    event: React.ChangeEvent<HTMLInputElement>
  ) =>
    handleToggleCreditsOverdraft(
      event,
      setIsCreditsOverdraftChecked,
      overdraftInputValue,
      setOverdraftInputValue
    );

  const driversSettings = generateDriversSettings({
    addNewDriver: settings[COMPANY_SETTINGS.AutomaticDriverRegistrationEnabled],
    handleToggleAddNewDriver: onToggle(
      COMPANY_SETTINGS.AutomaticDriverRegistrationEnabled
    ),

    autoDriverFileDownload: settings[COMPANY_SETTINGS.AutoDriverFileDownload],
    handleToggleAutoDriverFileDownload: onToggle(
      COMPANY_SETTINGS.AutoDriverFileDownload
    ),

    assignGlobalScheduleToDriver:
      settings[COMPANY_SETTINGS.AutoAssignGlobalScheduleToDriver],
    handleToggleAssignGlobalScheduleToDriver: onToggle(
      COMPANY_SETTINGS.AutoAssignGlobalScheduleToDriver
    ),

    isDisabled: !canManageCompaniesSettings
  });

  const vehiclesSettings = generateVehiclesSettings({
    assignVehicleRegistrationNumber:
      settings[COMPANY_SETTINGS.AutoAssignVehicleRegistrationNumber],
    handleToggleAssignVehicleRegistrationNumber: onToggle(
      COMPANY_SETTINGS.AutoAssignVehicleRegistrationNumber
    ),

    autoInitialVehicleDownload:
      settings[COMPANY_SETTINGS.AutoInitialVehicleDownload],
    handleToggleAutoInitialVehicleDownload: onToggle(
      COMPANY_SETTINGS.AutoInitialVehicleDownload
    ),

    assignGlobalScheduleToVehicle:
      settings[COMPANY_SETTINGS.AutoAssignGlobalScheduleToVehicle],
    handleToggleAssignGlobalScheduleToVehicle: onToggle(
      COMPANY_SETTINGS.AutoAssignGlobalScheduleToVehicle
    ),

    isDisabled: !canManageCompaniesSettings
  });

  const creditsOverdraftSettings = generateCreditsOverdraftSettings({
    isCreditsOverdraftChecked,
    handleToggleCreditsOverdraft: onToggleCreditsOverdraft,
    overdraftInputValue,
    setOverdraftInputValue
  });

  return (
    <>
      <SettingsTypeWrapper type={t("Details##vehicles")}>
        {vehiclesSettings}
      </SettingsTypeWrapper>

      <SettingsTypeWrapper type={t("Details##drivers")}>
        {driversSettings}
      </SettingsTypeWrapper>

      {/* overDraftEnabled prop is returned from the api only if user is super admin and selected row is client company */}
      {hasOverdraftToggle && (
        <SettingsTypeWrapper type={t("Details##other")}>
          {creditsOverdraftSettings}
        </SettingsTypeWrapper>
      )}
    </>
  );
};

export default CompanySettings;
